import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, Shield, Info, Check, X, Search, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../providers/TranslationProvider';
import { COUNTRY_CODES, SCREENS } from '../utils/constants';
import LetterGlitch from '../components/LetterGlitch';
import Logo from '../icons/logo.svg';

const PhoneInputScreen = () => {
  const { navigateToScreen } = useUI();
  const { t } = useTranslation();

  // State
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [localError, setLocalError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const phoneInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Theme state
  const [isDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Filter countries
  const filteredCountries = useMemo(() => {
    if (!searchTerm.trim()) return COUNTRY_CODES;
    const term = searchTerm.toLowerCase();
    return COUNTRY_CODES.filter(country =>
      country.name.toLowerCase().includes(term) ||
      country.code.toString().includes(term) ||
      (country.native && country.native.toLowerCase().includes(term))
    );
  }, [searchTerm]);

  // Format phone number
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhoneNumber(value);
      setLocalError('');
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsCountryDropdownOpen(false);
    setSearchTerm('');
  };

  const handleContinue = () => {
    if (phoneNumber.length < 10) {
      setLocalError(t('signup.invalidPhone', 'Please enter a valid phone number.'));
      return;
    }

    setIsProcessing(true);
    // Simulate API call
    setTimeout(() => {
      setIsProcessing(false);
      navigateToScreen(SCREENS.VERIFY_CODE);
    }, 1000);
  };

  // Animation Variants
  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const inputBg = isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-blue-100 text-slate-800 placeholder-slate-400';
  const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

  return (
    <motion.div
      className={`relative w-full h-screen overflow-hidden flex flex-col items-center justify-center ${bgClass} ${textClass}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Dynamic Background */}
      <div className={`absolute inset-0 transition-colors duration-700 ${isDarkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-blue-50 to-white'}`}>
        {isDarkMode && (
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <LetterGlitch
              glitchColors={['#1e40af', '#3b82f6', '#60a5fa']}
              glitchSpeed={50}
              centerVignette={true}
              outerVignette={false}
              smooth={true}
            />
          </div>
        )}
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => navigateToScreen(SCREENS.SIGNUP)}
        className="absolute top-6 left-6 z-20 p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Back"
      >
        <ArrowLeft size={24} className={isDarkMode ? 'text-white' : 'text-slate-800'} />
      </motion.button>

      {/* Main Content */}
      <motion.div
        className={`relative z-10 w-full max-w-md p-8 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col items-center mb-6">
          <div className={`w-16 h-16 mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-800 shadow-lg shadow-blue-900/20' : 'bg-white shadow-xl shadow-blue-100'}`}>
            <Phone className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600">
            {t('signup.phoneTitle', 'Phone Number')}
          </h2>
          <p className={`text-sm mt-1 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('signup.phoneSubtitle', 'Enter your phone number to create an account')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Phone Input Group */}
          <div className="space-y-1">
            <label className={`text-xs ml-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {t('signup.phoneLabel', 'Mobile Number')}
            </label>
            <div className="flex gap-2">
              {/* Country Selector */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                  className={`flex items-center justify-between px-3 py-3.5 rounded-xl border min-w-[100px] transition-all ${inputBg}`}
                >
                  <span className="text-lg mr-2">{selectedCountry.flag}</span>
                  <span className="text-sm font-medium">+{selectedCountry.dialCode}</span>
                  <ChevronDown size={14} className="ml-1 opacity-70" />
                </button>

                {/* Dropdown */}
                <AnimatePresence>
                  {isCountryDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className={`absolute top-full left-0 mt-2 w-64 max-h-60 overflow-hidden rounded-xl border shadow-xl z-50 flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                        }`}
                    >
                      <div className="p-2 border-b border-gray-200/10">
                        <div className="relative">
                          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
                          <input
                            ref={searchInputRef}
                            autoFocus
                            type="text"
                            placeholder="Search country..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg outline-none bg-transparent ${isDarkMode ? 'text-white placeholder-slate-500' : 'text-slate-800 placeholder-slate-400'}`}
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {filteredCountries.map(country => (
                          <button
                            key={country.code}
                            onClick={() => handleCountrySelect(country)}
                            className={`w-full flex items-center px-4 py-3 text-left hover:bg-white/5 transition-colors ${selectedCountry.code === country.code ? 'bg-blue-500/10' : ''
                              }`}
                          >
                            <span className="text-xl mr-3">{country.flag}</span>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                {country.name}
                              </div>
                              <div className="text-xs opacity-50">+{country.dialCode}</div>
                            </div>
                            {selectedCountry.code === country.code && <Check size={14} className="text-blue-500" />}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Phone Input */}
              <div className="relative flex-1">
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={formatPhoneNumber(phoneNumber)}
                  onChange={handlePhoneChange}
                  className={`w-full px-5 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${inputBg}`}
                  placeholder="555 123 4567"
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {localError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
              >
                <AlertCircle size={16} />
                <span>{localError}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isProcessing}
            onClick={handleContinue}
            className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg relative overflow-hidden group ${isDarkMode
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30'
                : 'bg-blue-600 text-white shadow-blue-200'
              }`}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('common.processing', 'Processing...')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {t('common.continue', 'Continue')}
                <ChevronRight size={20} />
              </span>
            )}
          </motion.button>

          {/* Footer */}
          <div className="text-center">
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {t('signup.terms', 'By continuing, you agree to our Terms of Service and Privacy Policy.')}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PhoneInputScreen;